import type { BeadColor } from '../catalog/types';
import type { Pattern } from '../pipeline/pipeline';

/**
 * Détecte si `pattern.paletteColorIds` est aligné avec l'ordre canonique
 * de la palette de référence (HAMA_MIDI_COLORS). Si oui : no-op. Si non :
 * remappe les `cells[]` pour qu'elles référencent l'index canonique, et
 * remplace `paletteColorIds` par l'ordre canonique.
 *
 * Contexte :
 *   Une régression brève a fait que `NewPatternDialog` recevait `HAMA_DISPLAY`
 *   (ordre trié par teinte) au lieu de `HAMA_MIDI_COLORS` canonique. Le
 *   pipeline générait alors des `cells[]` indexées dans l'ordre trié.
 *   Au chargement, le renderer canvas utilise pourtant `HAMA_MIDI_COLORS`
 *   canonique → mauvaise correspondance index→couleur → ciel violet,
 *   ours vert (cf. bug rapporté).
 *
 *   Cette fonction migre les patrons affectés à la volée au load, sans
 *   regénération manuelle requise. Idempotente.
 */
export function migrateToCanonicalPalette(
  pattern: Pattern,
  canonical: ReadonlyArray<BeadColor>,
): Pattern {
  // 1) Détection rapide : `paletteColorIds[i] === canonical[i].id` ∀ i ?
  if (pattern.paletteColorIds.length === canonical.length) {
    let aligned = true;
    for (let i = 0; i < canonical.length; i++) {
      const expected = canonical[i];
      if (expected === undefined || pattern.paletteColorIds[i] !== expected.id) {
        aligned = false;
        break;
      }
    }
    if (aligned) return pattern;
  }

  // 2) Construit le mapping oldIdx → newCanonicalIdx via les IDs.
  const idToCanonical = new Map<string, number>();
  for (let i = 0; i < canonical.length; i++) {
    const c = canonical[i];
    if (c) idToCanonical.set(c.id, i);
  }

  const oldToNew = new Map<number, number>();
  for (let oldIdx = 0; oldIdx < pattern.paletteColorIds.length; oldIdx++) {
    const id = pattern.paletteColorIds[oldIdx];
    if (id === undefined) continue;
    const newIdx = idToCanonical.get(id);
    if (newIdx !== undefined) {
      oldToNew.set(oldIdx, newIdx);
    }
  }

  // 3) Remappe les cells. Préserve les sentinelles négatives (EMPTY = -1).
  const newCells = pattern.cells.map((idx) =>
    idx < 0 ? idx : (oldToNew.get(idx) ?? idx),
  );

  return {
    ...pattern,
    cells: newCells,
    paletteColorIds: canonical.map((c) => c.id),
  };
}
